const HEX6_RE = /^#([0-9a-fA-F]{6})$/;
const HEX3_RE = /^#([0-9a-fA-F]{3})$/;
const RGBA_RE =
  /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*([0-9.]+))?\s*\)$/i;

export type ParsedLinearGradient = {
  angle: number;
  color1: string;
  color2: string;
};

function clampByte(n: number): number {
  return Math.max(0, Math.min(255, Math.round(n)));
}

function componentToHex(n: number): string {
  return clampByte(n).toString(16).padStart(2, '0');
}

export function normalizeHex(input: string, fallback = '#000000'): string {
  const trimmed = input.trim();
  if (HEX6_RE.test(trimmed)) return trimmed.toLowerCase();
  const short = HEX3_RE.exec(trimmed);
  if (short) {
    const [r, g, b] = short[1].split('');
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return fallback;
}

export function rgbToHex(r: number, g: number, b: number): string {
  return `#${componentToHex(r)}${componentToHex(g)}${componentToHex(b)}`;
}

export function hexToRgba(hex: string, alpha: number): string {
  const normalized = normalizeHex(hex);
  const r = parseInt(normalized.slice(1, 3), 16);
  const g = parseInt(normalized.slice(3, 5), 16);
  const b = parseInt(normalized.slice(5, 7), 16);
  const a = Math.max(0, Math.min(1, alpha));
  return `rgba(${r}, ${g}, ${b}, ${Number(a.toFixed(2))})`;
}

export function parseRgba(value: string): { hex: string; alpha: number } | null {
  const match = RGBA_RE.exec(value.trim());
  if (!match) return null;
  const [, r, g, b, a] = match;
  return {
    hex: rgbToHex(Number(r), Number(g), Number(b)),
    alpha: a !== undefined ? Number(a) : 1,
  };
}

export function cssToHex(value: string, fallback = '#000000'): string {
  const trimmed = value.trim();
  if (HEX6_RE.test(trimmed) || HEX3_RE.test(trimmed)) return normalizeHex(trimmed, fallback);
  const rgba = parseRgba(trimmed);
  if (rgba) return rgba.hex;
  return fallback;
}

export function isLinearGradient(value: string): boolean {
  return value.trim().toLowerCase().startsWith('linear-gradient(');
}

function splitGradientStops(stopsPart: string): string[] {
  const stops: string[] = [];
  let current = '';
  let depth = 0;

  for (const char of stopsPart) {
    if (char === '(') depth += 1;
    if (char === ')') depth -= 1;
    if (char === ',' && depth === 0) {
      if (current.trim()) stops.push(current.trim());
      current = '';
      continue;
    }
    current += char;
  }

  if (current.trim()) stops.push(current.trim());
  return stops;
}

function stopColor(stop: string): string {
  return stop.trim().split(/\s+/)[0] ?? stop;
}

export function parseLinearGradient(value: string): ParsedLinearGradient | null {
  const trimmed = value.trim();
  const match = /^linear-gradient\s*\(\s*([0-9.]+)deg\s*,\s*(.+)\s*\)$/i.exec(trimmed);
  if (!match) return null;

  const stops = splitGradientStops(match[2]);
  if (stops.length < 2) return null;

  return {
    angle: Number(match[1]) || 0,
    color1: cssToHex(stopColor(stops[0])),
    color2: cssToHex(stopColor(stops[stops.length - 1])),
  };
}

export function buildLinearGradient(angle: number, color1: string, color2: string): string {
  const c1 = normalizeHex(color1);
  const c2 = normalizeHex(color2);
  const deg = Math.round(((angle % 360) + 360) % 360);
  return `linear-gradient(${deg}deg, ${c1}, ${c2})`;
}

export function detectColorFieldMode(value: string): 'solid' | 'gradient' {
  return isLinearGradient(value) ? 'gradient' : 'solid';
}
